'use client';

import { createContext, useContext, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from '@/lib/api';
import { encrypt } from './crypto';
import { RootState, setSession, clearSession } from './store';
import { useEffect } from 'react';

interface AuthContextType {
  sessionToken: string | null;
  userId: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const sessionToken = useSelector((state: RootState) => state.auth.sessionToken);
  const userId = useSelector((state: RootState) => state.auth.userId);

  // Listen for token expiry events from API client
  useEffect(() => {
    const handleTokenExpired = () => {
      dispatch(clearSession());
    };
    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, [dispatch]);

  const login = async (username: string, password: string) => {
    const encUser = await encrypt(username);
    const encPass = await encrypt(password);

    const data = await api.createSession(encUser, encPass);

    dispatch(setSession({ sessionToken: data.token, userId: username }));
  };

  const logout = () => {
    dispatch(clearSession());
  };

  return (
    <AuthContext.Provider
      value={{
        sessionToken,
        userId,
        isAuthenticated: !!sessionToken,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
